import { defineStore } from "pinia";
import { computed, reactive, ref, toRefs, watch } from "vue";
import { isBasketRepoError } from "@chatfood/dbe-client/errors";
import { useAnalytics, useDirectBackend, useRuntimeConfig } from "#imports";
import { useAppURI } from "~/composables/use-app-uri";
import { useMenusStore } from "~/stores/menus";
import { useOutletStore } from "~/stores/outlet";
import type { IDomainAppliedLoyaltyWallet } from "~/domain/applied-loyalty-wallet";
import type { IDomainBasket } from "~/domain/basket";
import type { IDomainBasketSummary } from "~/domain/basket-summary";
import { CHECKOUT_CALLBACK_PARAM } from "~/domain/checkout-callback-param";
import type {
  DeliveryAddress,
  DeliveryLocation,
} from "~/domain/delivery-location";
import type { IDomainGiftCard } from "~/domain/gift-card";
import type { IDomainMoney } from "~/domain/money";
import type { IDomainOrderScheduledTime } from "~/domain/order-scheduled-time";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { IDomainPaymentMethod } from "~/domain/payment-method";
import type { IDomainPickupPackaging } from "~/domain/pickup-packaging";
import { SalesChannel } from "~/domain/sales-channels";
import { getAvailableAt } from "~/utils/scheduling";

type PendingDeliveryLocation =
  Omit<DeliveryAddress, "type"> | Omit<DeliveryLocation, "type">;

type PendingBasketAction =
  | {
      type: "addItem";
      plu: string;
    }
  | {
      type: "addLoyaltyProgram";
      loyaltyProgramId: string;
    };

// Define the state interface
type IState = IDomainBasket & {
  isLoaded: boolean;
  isProcessing: boolean;
  processingLineItemId?: string;
  pendingDeliveryLocation?: PendingDeliveryLocation;
  pendingActions: PendingBasketAction[];
};

// Helper function to create the initial state
function createInitialState(salesChannelDefault: SalesChannel): IState {
  const currency: string = "USD";

  const zero: IDomainMoney = { value: 0, currency };

  return {
    isLoaded: false,
    isProcessing: false,
    processingLineItemId: undefined,
    pendingDeliveryLocation: undefined,
    pendingActions: [],
    id: "",
    orderingMode: "delivery",
    businessId: "",
    outletId: "",
    salesChannel: salesChannelDefault,
    currency,
    items: [],
    itemLimits: null,
    giftCard: undefined,
    addressId: undefined,
    currentLocation: undefined,
    deliveryCoverage: undefined,
    discount: undefined,
    scheduledTime: undefined,
    totalBreakdown: {
      serviceCharges: zero,
      bagFee: zero,
      loyaltyRedeemed: zero,
      taxAmount: zero,
      deliveryFee: zero,
      outstandingBalance: zero,
      discountedAmount: zero,
      basketTotal: zero,
      subtotal: zero,
    },
    total: zero,
    notes: "",
    loyalties: [],
    loyaltyWallet: undefined,
    payments: [],
    eventName: undefined,
    pax: undefined,
    vehicleInfo: undefined,
  };
}

function ensureBasketIsNotLocked(state: IState) {
  if (!state.isLoaded) {
    throw new Error("[BasketStore] Basket is not loaded.");
  }

  if (state.isProcessing) {
    throw new Error("[BasketStore] Basket is in lock state at the moment.");
  }
}

export const useBasketStore = defineStore("basket", () => {
  const salesChannelDefault = useRuntimeConfig().public.defaultCateringChannel
    ? SalesChannel.CATERING
    : SalesChannel.WEB_ORDERING;

  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState(salesChannelDefault));

  const itemsSummary = computed<Record<string, number>>(() => {
    const response: Record<string, number> = {};

    return state.items.reduce((summary, item) => {
      summary[item.itemId] = (summary[item.itemId] ?? 0) + item.quantity;
      return summary;
    }, response);
  });

  const totalQuantity = computed(() =>
    state.items.reduce((sum, item) => sum + item.quantity, 0),
  );

  const isForPickup = computed(() => state.orderingMode === "pickup");

  const isForDelivery = computed(() => state.orderingMode === "delivery");

  const isForCurbside = computed(() => state.orderingMode === "curbside");

  const discountCode = computed(() => state.discount?.code);

  const giftCardPayment = computed(
    () => state.payments.find((p) => p.type === "gift_card")?.amount,
  );

  /**
   * The time menus and items must be resolved against: the scheduled slot, or
   * `undefined` for ASAP. Never a materialised "now" — callers stamp that at
   * request time so it cannot go stale.
   */
  const fulfillmentTime = computed(() =>
    getAvailableAt(state.scheduledTime, useOutletStore().timezone),
  );

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState(salesChannelDefault));
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  function setPendingDeliveryLocation(location?: PendingDeliveryLocation) {
    state.pendingDeliveryLocation = location;
  }

  async function applyPendingDeliveryLocation() {
    const location = state.pendingDeliveryLocation;

    if (!location) return;

    if ("addressId" in location) {
      await setAddress(location);
    } else {
      await setLocation(location);
    }

    if (state.addressId || state.currentLocation) {
      setPendingDeliveryLocation(undefined);
    }
  }

  function addPendingAction(action: PendingBasketAction) {
    state.pendingActions.push(action);
  }

  async function addItemByPlu(plu: string) {
    const { menuRepo } = useDirectBackend();
    const menusStore = useMenusStore();

    try {
      const response = await menuRepo.getItems({
        plus: [plu],
        outletId: state.outletId,
        menuIds: menusStore.menuIds,
        salesChannel: state.salesChannel,
      });

      const item = response.data[0];

      if (!item) {
        throw new Error("Item not found");
      }

      await addItem({
        id: item.id,
        quantity: 1,
        unitPrice: item.price.value,
        notes: "",
        menuId: item.menuId,
        plu: item.plu,
        modifierLists: [],
      });
    } catch {
      // Fail silently
    }
  }

  async function executePendingActions() {
    const addItemAction = state.pendingActions.find(
      (action) => action.type === "addItem",
    );

    if (addItemAction) {
      await addItemByPlu(addItemAction.plu);
    }

    const loyaltyAction = state.pendingActions.find(
      (action) => action.type === "addLoyaltyProgram",
    );

    if (loyaltyAction) {
      try {
        await applyLoyalty(loyaltyAction.loyaltyProgramId);
      } catch {
        // Fail silently
      }
    }

    state.pendingActions = [];
  }

  function lockBasket(processingLineId?: string) {
    state.isProcessing = true;
    state.processingLineItemId = processingLineId;
  }

  function releaseBasketLock() {
    state.isProcessing = false;
    state.processingLineItemId = undefined;
  }

  async function setAddress(location: Omit<DeliveryAddress, "type">) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const response = await basketRepo.setDeliveryLocation({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        deliveryLocation: {
          ...location,
          type: "address",
        },
      });

      if (!response) return;

      patch({
        currentLocation: undefined,
        ...response,
      });
    } finally {
      releaseBasketLock();
    }
  }

  async function setLocation(location: Omit<DeliveryLocation, "type">) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const response = await basketRepo.setDeliveryLocation({
        outletId: state.outletId,
        businessId: state.businessId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        deliveryLocation: {
          ...location,
          type: "location",
        },
      });

      if (!response) return;

      patch({
        addressId: undefined,
        ...response,
      });
    } finally {
      releaseBasketLock();
    }
  }

  async function setNotes(notes: string) {
    const res = await basketRepo.setNote({
      businessId: state.businessId,
      outletId: state.outletId,
      salesChannel: state.salesChannel,
      orderingMode: state.orderingMode,
      notes,
    });

    if (!res) return;

    patch({ notes });
  }

  async function setVehicleInfo(
    vehicleInfo: NonNullable<IDomainBasket["vehicleInfo"]>,
  ) {
    ensureBasketIsNotLocked(state);

    const response = await basketRepo.setVehicleInfo({
      basketId: state.id,
      ...vehicleInfo,
    });

    if (!response) return;

    patch({ ...response });
  }

  async function setScheduledTime(value?: IDomainOrderScheduledTime) {
    // Safe deep comparison to avoid JSON.stringify recursion issues
    const currentTime = state.scheduledTime;
    if (
      (!currentTime && !value) ||
      (currentTime &&
        value &&
        currentTime.day === value.day &&
        currentTime.time === value.time)
    ) {
      return;
    }

    ensureBasketIsNotLocked(state);

    try {
      const response = await basketRepo.setScheduledTime({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        scheduledAt: value,
      });

      if (response) {
        patch({ ...response, scheduledTime: response.scheduledTime });
      }
    } finally {
      releaseBasketLock();
    }
  }

  async function applyDiscount(discountCode: string) {
    ensureBasketIsNotLocked(state);

    lockBasket();

    try {
      const res = await basketRepo.applyDiscount({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        discountCode,
      });

      if (!res) return;

      patch(res);
    } finally {
      releaseBasketLock();
    }
  }

  async function removeDiscount() {
    ensureBasketIsNotLocked(state);

    lockBasket();

    try {
      const res = await basketRepo.removeDiscount({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
      });

      if (!res) return;

      patch({ discount: undefined, ...res });
    } finally {
      releaseBasketLock();
    }
  }

  async function removeDiscountAndItem(lineId: string) {
    await removeDiscount();

    const findItem = state.items.find((item) => item.lineId === lineId);

    if (!findItem) return;

    await updateItemQuantity(lineId, 0);
  }

  const { basketRepo } = useDirectBackend();

  const summaries = ref<IDomainBasketSummary[]>([]);

  function setSummaries(list: IDomainBasketSummary[]) {
    summaries.value = list;
  }

  async function loadBasket(
    businessId: string,
    currency: string,
    outletId: string,
    orderingMode: IDomainOrderingMode,
    salesChannel: SalesChannel,
    scheduledTime?: IDomainOrderScheduledTime,
  ) {
    if (
      state.isLoaded &&
      state.id &&
      state.businessId === businessId &&
      state.outletId === outletId &&
      state.orderingMode === orderingMode &&
      state.salesChannel === salesChannel
    ) {
      return;
    }

    const createParams = {
      businessId,
      outletId,
      orderingMode,
      salesChannel,
      currency,
      ...(scheduledTime && { scheduledAt: scheduledTime }),
    };

    const createAndTrack = async (): Promise<IDomainBasket> => {
      const result = await basketRepo.createBasket(createParams);
      summaries.value.push({
        basketId: result.id,
        businessId,
        outletId,
        orderingMode,
        salesChannel,
        type: "solo",
      });
      return result;
    };

    const match = summaries.value.find(
      (s) =>
        s.outletId === outletId &&
        s.orderingMode === orderingMode &&
        s.salesChannel === salesChannel,
    );

    let response: IDomainBasket;

    if (match) {
      try {
        response = await basketRepo.getBasket(match.basketId);
      } catch (error) {
        if (!isBasketRepoError(error) || !error.isNotFound()) throw error;
        summaries.value = summaries.value.filter(
          (s) => s.basketId !== match.basketId,
        );
        response = await createAndTrack();
      }
    } else {
      response = await createAndTrack();
    }

    const pendingActions = state.pendingActions;

    patch({
      ...createInitialState(salesChannelDefault),
      ...response,
      isLoaded: true,
      pendingActions,
    });

    if (state.pendingActions.length) {
      await executePendingActions();
    }
  }

  async function loadBasketWithLocation(params: {
    businessId: string;
    currency: string;
    outletId: string;
    orderingMode: IDomainOrderingMode;
    salesChannel: SalesChannel;
    location?: PendingDeliveryLocation;
    scheduledTime?: IDomainOrderScheduledTime;
  }) {
    await loadBasket(
      params.businessId,
      params.currency,
      params.outletId,
      params.orderingMode,
      params.salesChannel,
      params.scheduledTime,
    );

    if (!params.location) return;

    if ("addressId" in params.location) {
      await setAddress(params.location);
    } else {
      await setLocation(params.location);
    }
  }

  async function revalidate() {
    if (!state.isLoaded) return;

    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const params = state.id
        ? state.id
        : {
            businessId: state.businessId,
            outletId: state.outletId,
            orderingMode: state.orderingMode,
            salesChannel: state.salesChannel,
            currency: state.currency,
          };

      const response = await basketRepo.getBasket(params);
      const pendingActions = state.pendingActions;

      patch({
        ...createInitialState(salesChannelDefault),
        ...response,
        isLoaded: true,
        pendingActions,
      });
    } finally {
      releaseBasketLock();
    }
  }

  function getCheckoutCallbackUrl(): string {
    const callbackURL = new URL(window.location.href);
    callbackURL.searchParams.set(CHECKOUT_CALLBACK_PARAM, "{orderId}");

    const { retrieveCallbackURI } = useAppURI();
    const callbackAppURI = retrieveCallbackURI(callbackURL.toString());

    if (callbackAppURI) {
      return callbackAppURI.toString();
    }

    return callbackURL.toString();
  }

  async function checkout(
    paymentMethod: IDomainPaymentMethod,
    tipAmount = 0,
    packagingType?: IDomainPickupPackaging,
  ) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const response = await basketRepo.checkout({
        businessId: state.businessId,
        outletId: state.outletId,
        salesChannel: state.salesChannel,
        orderingMode: state.orderingMode,
        callbackUrl: getCheckoutCallbackUrl(),
        paymentMethod,
        tipAmount,
        packagingType,
      });

      return response;
    } finally {
      releaseBasketLock();
    }
  }

  async function applyGiftCard(giftCard: IDomainGiftCard) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const response = await basketRepo.setGiftCard({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        giftCard: {
          providerId: giftCard.providerId,
          cardNumber: giftCard.cardNumber,
          amount: giftCard.amount,
          pin: giftCard.pin || "",
        },
      });

      patch({ ...response });
    } finally {
      releaseBasketLock();
    }
  }

  async function removeGiftCard() {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const res = await basketRepo.setGiftCard({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
      });

      patch({ giftCard: undefined, ...res });
    } finally {
      releaseBasketLock();
    }
  }

  type AddItemResponse = Awaited<ReturnType<typeof basketRepo.addItem>>;

  async function addItem(item: AddItemResponse["item"]) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const res = await basketRepo.addItem({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        item,
      });

      if (!res) return;

      patch({
        ...res,
        deliveryCoverage: res.deliveryCoverage ?? state.deliveryCoverage,
      });
    } finally {
      releaseBasketLock();
    }
  }

  async function updateItemQuantity(itemLineId: string, newQuantity: number) {
    ensureBasketIsNotLocked(state);
    lockBasket(itemLineId);

    try {
      const res = await basketRepo.updateItemQuantity({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        lineItemId: itemLineId,
        quantity: newQuantity,
      });

      if (!res) return;

      patch({
        ...res,
        deliveryCoverage: res.deliveryCoverage ?? state.deliveryCoverage,
      });
    } finally {
      releaseBasketLock();
    }
  }

  async function removeBasket() {
    lockBasket();

    try {
      await basketRepo.deleteBasket({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
      });

      resetAndRemoveFromSummary();
    } catch (error) {
      if (isBasketRepoError(error) && error.isNotFound()) {
        resetAndRemoveFromSummary();
      }
    } finally {
      releaseBasketLock();
    }
  }

  function resetAndRemoveFromSummary() {
    if (state.id) {
      summaries.value = summaries.value.filter((s) => s.basketId !== state.id);
    }

    reset();
  }

  async function revalidateDiscount() {
    if (!state.discount) return;

    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const currentDiscountCode = state.discount.code;

      await basketRepo.removeDiscount({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
      });

      const res = await basketRepo.applyDiscount({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        discountCode: currentDiscountCode,
      });

      patch(res);
    } catch {
      patch({ discount: undefined });
    } finally {
      releaseBasketLock();
    }
  }

  async function applyLoyalty(loyaltyProgramId: string) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const res = await basketRepo.applyLoyaltyProgram({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        loyaltyProgramId,
      });

      if (!res) return;

      patch({ discount: undefined, ...res });
    } finally {
      releaseBasketLock();
    }
  }

  async function removeLoyalty(loyaltyProgramId: string) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const res = await basketRepo.removeLoyaltyProgram({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        loyaltyProgramId,
      });

      patch(res);
    } finally {
      releaseBasketLock();
    }
  }

  async function applyLoyaltyWalletCash(
    amount: IDomainAppliedLoyaltyWallet["amount"],
  ) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const response = await basketRepo.setLoyaltyWalletCash({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        amount: amount.value,
      });

      patch({ discount: undefined, ...response });
    } finally {
      releaseBasketLock();
    }
  }

  async function removeLoyaltyWallet() {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const response = await basketRepo.setLoyaltyWalletCash({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
      });

      patch({ loyaltyWallet: undefined, ...response });
    } finally {
      releaseBasketLock();
    }
  }

  async function setMeta(
    meta: Parameters<typeof basketRepo.setMeta>[0]["meta"],
  ) {
    ensureBasketIsNotLocked(state);
    lockBasket();

    try {
      const response = await basketRepo.setMeta({
        businessId: state.businessId,
        outletId: state.outletId,
        orderingMode: state.orderingMode,
        salesChannel: state.salesChannel,
        meta,
      });

      if (!response) return;

      patch(response);
    } finally {
      releaseBasketLock();
    }
  }

  async function recreateBasket(orderId: string, channelLinkId: string) {
    lockBasket();

    try {
      const response = await basketRepo.recreateBasket({
        orderId,
        channelLinkId,
      });

      if (!response) return;

      reset();
      patch({
        ...response,
        isLoaded: true,
      });
    } finally {
      releaseBasketLock();
    }
  }

  const { setSuperProperties } = useAnalytics();
  watch(
    () => state.orderingMode,
    () => {
      setSuperProperties({
        orderingMode: state.orderingMode || undefined,
      });
    },
  );

  return {
    ...toRefs(state),
    state: computed(() => state),
    itemsSummary,
    totalQuantity,
    giftCardPayment,
    isForPickup,
    isForDelivery,
    isForCurbside,
    discountCode,
    fulfillmentTime,
    summaries,
    patch,
    reset,
    setSummaries,
    loadBasket,
    loadBasketWithLocation,
    revalidate,
    addItem,
    checkout,
    updateItemQuantity,
    applyDiscount,
    removeDiscount,
    removeDiscountAndItem,
    removeBasket,
    setAddress,
    applyGiftCard,
    setLocation,
    setNotes,
    setVehicleInfo,
    setScheduledTime,
    setPendingDeliveryLocation,
    applyPendingDeliveryLocation,
    addPendingAction,
    executePendingActions,
    addItemByPlu,
    removeGiftCard,
    revalidateDiscount,
    applyLoyalty,
    removeLoyalty,
    applyLoyaltyWalletCash,
    removeLoyaltyWallet,
    setMeta,
    recreateBasket,
  };
});
